import React, { useState } from 'react';
import './DragDropDivergent.css';

function DragDropDivergent({ pregunta, respuestas, onSelectDropZone, disabled, showCorrectAnswer = false, respuestaCorrecta = null }) {
  // Los hooks deben estar siempre al inicio, antes de cualquier return condicional
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [hoveredZoneId, setHoveredZoneId] = useState(null);

  // Validación de seguridad (después de los hooks)
  if (!pregunta || !pregunta.id) {
    return (
      <div className="dragdrop-divergent-container">
        <div className="dragdrop-divergent-error">
          <p>Error: Invalid question.</p>
        </div>
      </div>
    );
  }

  // Obtener los datos de drag_drop_data
  const dragDropData = pregunta.drag_drop_data || {};
  const draggableItems = dragDropData.draggable_items || [];
  const dropZonesLayout = dragDropData.drop_zones_layout || {};

  // Obtener respuesta actual para esta pregunta
  const respuestaDragDrop = respuestas[pregunta.id] || {};

  // Manejar inicio del arrastre
  const handleDragStart = (e, itemId) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId.toString());
  };

  // Manejar cuando se arrastra sobre una zona de destino
  const handleDragOver = (e, zoneId) => {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setHoveredZoneId(zoneId);
  };

  // Manejar cuando se sale de una zona
  const handleDragLeave = () => {
    setHoveredZoneId(null);
  };

  // Manejar cuando se suelta en una zona de destino
  const handleDrop = (e, dropZoneId) => {
    if (disabled || !draggedItemId) return;
    
    e.preventDefault();
    const draggedId = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (!isNaN(draggedId)) {
      onSelectDropZone(pregunta.id, dropZoneId, draggedId);
    }
    
    setDraggedItemId(null);
    setHoveredZoneId(null);
  };

  // Manejar cuando se arrastra fuera de las zonas de destino
  const handleDragEnd = () => {
    setDraggedItemId(null);
    setHoveredZoneId(null);
  };

  // Obtener el item arrastrable que está en una zona de destino específica
  const getItemInDropZone = (dropZoneId) => {
    const itemId = respuestaDragDrop[`drop_zone_${dropZoneId}`];
    if (!itemId) return null;
    return draggableItems.find(item => item.id === itemId);
  };

  // Verificar si un item puede usarse múltiples veces (es respuesta correcta en múltiples drop_zones)
  const canItemBeUsedMultipleTimes = (itemId) => {
    if (!respuestaCorrecta || typeof respuestaCorrecta !== 'object') {
      return false;
    }
    // Contar cuántas zonas tienen este item como respuesta correcta
    const correctZonesCount = Object.values(respuestaCorrecta).filter(correctId => correctId === itemId).length;
    return correctZonesCount > 1;
  };

  // Verificar si un item está siendo usado en alguna zona de destino
  // Si el item puede usarse múltiples veces, no lo bloqueamos
  const isItemUsed = (itemId) => {
    if (canItemBeUsedMultipleTimes(itemId)) {
      // Si puede usarse múltiples veces, nunca está "bloqueado"
      return false;
    }
    // Si solo puede usarse una vez, verificar si ya está en uso
    return Object.values(respuestaDragDrop).includes(itemId);
  };

  // Manejar eliminación de un item de una zona de destino
  const handleRemoveFromDropZone = (dropZoneId) => {
    if (disabled) return;
    onSelectDropZone(pregunta.id, dropZoneId, null);
  };

  // Renderizar una línea del layout
  const renderLinea = (linea, lineIndex) => {
    if (linea.tipo === 'texto') {
      return (
        <div key={lineIndex} className="dragdrop-divergent-line">
          <span className="dragdrop-divergent-text">{linea.valor}</span>
        </div>
      );
    } else if (linea.tipo === 'mixta' && linea.elementos) {
      return (
        <div key={lineIndex} className="dragdrop-divergent-line">
          {linea.elementos.map((elemento, elemIndex) => {
            if (elemento.tipo === 'texto') {
              return (
                <span key={elemIndex} className="dragdrop-divergent-text">
                  {elemento.valor}
                </span>
              );
            } else if (elemento.tipo === 'drop_zone') {
              const dropZoneId = elemento.drop_zone_id;
              const itemInZone = getItemInDropZone(dropZoneId);
              const hasItem = itemInZone !== null;
              const isHovered = hoveredZoneId === dropZoneId;
              const correctItemId = showCorrectAnswer && respuestaCorrecta ? respuestaCorrecta[`drop_zone_${dropZoneId}`] : null;
              const correctItem = correctItemId != null ? draggableItems.find((i) => i.id === correctItemId) : null;
              const esCorrecto = correctItemId != null && itemInZone?.id === correctItemId;
              const esIncorrecto = showCorrectAnswer && correctItemId != null && hasItem && itemInZone?.id !== correctItemId;
              const zoneFeedbackClass = showCorrectAnswer && correctItemId != null
                ? esCorrecto
                  ? ' dragdrop-divergent-zone-correct'
                  : esIncorrecto
                  ? ' dragdrop-divergent-zone-incorrect'
                  : ' dragdrop-divergent-zone-sin-responder'
                : '';

              return (
                <span
                  key={elemIndex}
                  className={`dragdrop-divergent-zone-inline ${hasItem ? 'dragdrop-divergent-zone-filled' : ''} ${!showCorrectAnswer && isHovered && !hasItem ? 'dragdrop-divergent-zone-hover' : ''}${zoneFeedbackClass}`}
                  onDragOver={!showCorrectAnswer ? (e) => handleDragOver(e, dropZoneId) : undefined}
                  onDragLeave={!showCorrectAnswer ? handleDragLeave : undefined}
                  onDrop={!showCorrectAnswer ? (e) => handleDrop(e, dropZoneId) : undefined}
                >
                  {hasItem ? (
                    <span className="dragdrop-divergent-zone-item-inline">
                      {itemInZone.texto}
                      {!showCorrectAnswer && !disabled && (
                        <button
                          className="dragdrop-divergent-zone-remove-inline"
                          onClick={() => handleRemoveFromDropZone(dropZoneId)}
                          type="button"
                          title="Remove"
                        >
                          ×
                        </button>
                      )}
                      {showCorrectAnswer && esIncorrecto && <span className="dragdrop-divergent-zone-feedback"> ✗</span>}
                      {showCorrectAnswer && esCorrecto && <span className="dragdrop-divergent-zone-feedback"> ✓</span>}
                    </span>
                  ) : (
                    <span className="dragdrop-divergent-zone-placeholder-inline">
                      {showCorrectAnswer && correctItem ? correctItem.texto : '...'}
                    </span>
                  )}
                  {showCorrectAnswer && correctItem && (!hasItem || esIncorrecto) && (
                    <span className="dragdrop-divergent-zone-correct-answer"> (Correct: {correctItem.texto})</span>
                  )}
                </span>
              );
            }
            return null;
          })}
        </div>
      );
    }
    return null;
  };

  // Validar datos
  if (!dropZonesLayout.lineas || dropZonesLayout.lineas.length === 0 || draggableItems.length === 0) {
    return (
      <div className="dragdrop-divergent-container">
        <div className="dragdrop-divergent-error">
          <p>Error: This question does not have drag & drop data configured correctly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dragdrop-divergent-container">
      <div className="dragdrop-divergent-wrapper">
        {/* Columna izquierda: Items arrastrables */}
        <div className="dragdrop-divergent-source-column">
          <div className="dragdrop-divergent-column-header">
            <h3>{dragDropData.titulo_source_column || "Values"}</h3>
          </div>
          <div className="dragdrop-divergent-items-list">
            {draggableItems.map((item) => {
              const used = isItemUsed(item.id);
              const isDragging = draggedItemId === item.id;
              const canUseMultiple = canItemBeUsedMultipleTimes(item.id);
              const isCurrentlyInUse = Object.values(respuestaDragDrop).includes(item.id);
              
              return (
                <div
                  key={item.id}
                  className={`dragdrop-divergent-item ${used && !canUseMultiple ? 'dragdrop-divergent-item-used' : ''} ${isDragging ? 'dragdrop-divergent-item-dragging' : ''}`}
                  draggable={!disabled && !used}
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  onDragEnd={handleDragEnd}
                  title={used && !canUseMultiple ? 'Already in use' : canUseMultiple && isCurrentlyInUse ? 'Can be used multiple times' : 'Drag to drop'}
                >
                  <div className="dragdrop-divergent-item-content">
                    <span className="dragdrop-divergent-item-text">{item.texto}</span>
                    {used && !canUseMultiple && <span className="dragdrop-divergent-item-used-badge">✓</span>}
                    {canUseMultiple && isCurrentlyInUse && <span className="dragdrop-divergent-item-multiple-badge" title="This item can be used in multiple zones">↻</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Columna derecha: Layout de líneas con drop zones */}
        <div className="dragdrop-divergent-target-column">
          <div className="dragdrop-divergent-column-header">
            <h3>Answer Area</h3>
          </div>
          <div className="dragdrop-divergent-layout-container">
            {dropZonesLayout.lineas.map((linea, index) => renderLinea(linea, index))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DragDropDivergent;

